import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/contexts/AppContext";

import { Icon } from "@/components/store/Icon";
import { LanguageToggle } from "@/components/LanguageToggle";
import { cartQuery, favoriteIdsQuery } from "@/lib/api/queries";
import seenLogo from "@/assets/seen-logo.png";

const NAV_LINKS = [
  { key: "home", to: "/", labelKey: "nav.home" },
  { key: "shop", to: "/shop", labelKey: "nav.shop" },
  { key: "contact", to: "/#contact", labelKey: "nav.contact" },
  { key: "returns", to: "/returns", labelKey: "nav.returns", isMuted: true },
] as const;

import { useNavigate } from "react-router-dom";

/** Animated hamburger that morphs into an X. */
function MenuIcon({ open }: { open: boolean }) {
  const bar = "absolute h-[2px] w-6 rounded-full bg-current";
  return (
    <span className="relative block h-6 w-6" aria-hidden="true">
      <motion.span
        className={bar}
        initial={false}
        animate={open ? { top: 11, rotate: 45 } : { top: 5, rotate: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 28 }}
        style={{ left: 0 }}
      />
      <motion.span
        className={bar}
        initial={false}
        animate={open ? { opacity: 0, scaleX: 0.4 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.18 }}
        style={{ left: 0, top: 11 }}
      />
      <motion.span
        className={bar}
        initial={false}
        animate={open ? { top: 11, rotate: -45 } : { top: 17, rotate: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 28 }}
        style={{ left: 0 }}
      />
    </span>
  );
}

/** The single site-wide navigation bar — identical on every route. */
export function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useApp();
  const { data: cart } = useQuery(cartQuery());
  const fq = favoriteIdsQuery();
  const { data: favoriteIds } = useQuery<string[]>({ queryKey: fq.queryKey, queryFn: fq.queryFn, enabled: !!user });
  const count = cart?.items?.reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const favoriteCount = favoriteIds?.length ?? 0;


  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isActive = (to: string) => pathname === to || (to === "/shop" && pathname.startsWith("/product"));

  /** Hash links scroll to the home contact section instead of doing a hard nav. */
  const handleNavClick = (e: React.MouseEvent, to: string) => {
    if (!to.includes("#")) return;
    const hash = to.split("#")[1];
    if (pathname === "/") {
      e.preventDefault();
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      id="main-header"
      className={`fixed top-3 left-3 right-3 md:top-4 md:left-[3%] md:right-[3%] z-50 mx-auto max-w-[1360px] w-[calc(100%-1.5rem)] md:w-[94%] bg-background/90 backdrop-blur-md border border-outline-variant/30 rounded-2xl overflow-hidden transition-all duration-300 ${
        scrolled ? "shadow-lg border-primary/20" : "shadow-md"
      }`}
    >
      <div className="flex items-center justify-between w-full px-grid-margin py-1 max-w-[1440px] mx-auto">
        <div className="flex-1 flex justify-start items-center min-w-0">
          <Link to="/" aria-label="SEEN" className="shrink-0">
            <img src={seenLogo} alt="SEEN" className="h-10 sm:h-11 md:h-14 w-auto object-contain" />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV_LINKS.map((link) => {
            const muted = "isMuted" in link && Boolean(link.isMuted);
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => handleNavClick(e, link.to)}
                className={`relative uppercase transition-colors duration-300 ${
                  muted
                    ? `text-[11px] font-normal tracking-wider text-on-surface-variant/45 hover:text-on-surface-variant/80 ${
                        isActive(link.to) ? "text-primary/70 font-medium" : ""
                      }`
                    : `text-nav font-nav hover:text-primary after:absolute after:-bottom-1.5 after:start-0 after:h-[2px] after:bg-secondary after:transition-all after:duration-300 ${
                        isActive(link.to)
                          ? "text-primary after:w-full"
                          : "text-on-surface-variant/80 after:w-0 hover:after:w-full"
                      }`
                }`}
              >
                {t(link.labelKey)}
              </Link>
            );
          })}
          {user && (user.role === "admin" || user.role === "super_admin") && (
            <Link
              to="/admin"
              className="text-nav font-nav transition-colors duration-300 uppercase hover:text-primary text-secondary"
            >
              {t("admin.title")}
            </Link>
          )}
        </nav>


        <div className="flex-1 flex justify-end items-center gap-3 sm:gap-5">
          <LanguageToggle />

          <div className="relative">
            <Link
              to="/favorites"
              aria-label={t("nav.favorites")}
              className={`transition-colors duration-300 hover:text-golden-latte ${
                favoriteCount > 0 ? "text-golden-latte" : "text-on-background"
              }`}
            >
              <Icon name="favorite" />
            </Link>
            {favoriteCount > 0 && (
              <span className="absolute -top-2 -end-2 bg-secondary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {favoriteCount}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => navigate(user ? (['admin', 'super_admin', 'operator'].includes(user.role ?? '') ? '/admin' : '/profile') : '/auth')}
            aria-label={t("nav.profile")}
            className="text-on-background hover:text-golden-latte transition-colors duration-300"
          >
            <Icon name="person" />
          </button>

          <div className="relative">
            <Link
              to="/cart"
              aria-label={t("nav.cart")}
              className="text-on-background hover:text-golden-latte transition-colors duration-300"
            >
              <Icon name="shopping_bag" />
            </Link>
            {count > 0 && (
              <span className="absolute -top-2 -end-2 bg-secondary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </div>

          <button
            type="button"
            aria-label={t("nav.menu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="lg:hidden text-on-background"
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {menuOpen && (
          <motion.nav
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-outline-variant/30 bg-background"
          >
            <div className="px-grid-margin py-5 flex flex-col gap-5">
              {NAV_LINKS.map((link, i) => {
                const muted = "isMuted" in link && Boolean(link.isMuted);
                return (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ delay: 0.06 + i * 0.05, duration: 0.25 }}
                  >
                    <Link
                      to={link.to}
                      onClick={(e) => { handleNavClick(e, link.to); setMenuOpen(false); }}
                      className={`block uppercase ${
                        muted
                          ? `text-xs tracking-wider text-on-surface-variant/45 hover:text-on-surface-variant/80 ${
                              isActive(link.to) ? "text-primary font-medium" : ""
                            }`
                          : `text-nav font-nav ${
                              isActive(link.to) ? "text-primary" : "text-on-surface-variant/80"
                            }`
                      }`}
                    >
                      {t(link.labelKey)}
                    </Link>
                  </motion.div>
                );
              })}
              {user && (user.role === "admin" || user.role === "super_admin") && (
                <motion.div
                  key="/admin"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ delay: 0.06 + NAV_LINKS.length * 0.05, duration: 0.25 }}
                >
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="block text-nav font-nav uppercase text-secondary hover:text-primary"
                  >
                    {t("admin.title")}
                  </Link>
                </motion.div>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
